/**
 * The money area of the phone.
 *
 * `MoneyHome` is the whole UI and takes its data and its writes as props, so it
 * can be driven from a fixture. `MoneyScreen` is the thin wrapper that binds it
 * to the local mirror and the outbox — the only place in this directory that
 * knows a database exists.
 */

import { type KeyboardEvent, useMemo, useRef, useState } from 'react'
import type { Bill, BillInstance, MemberId, Piastres, TrustedClock } from '@office/domain'
import type { OfficeDb } from '../../lib/db.js'
import { type MoneyActions, actionsFromMutations } from './actions.js'
import { type MoneySnapshot, useMoneySnapshot } from './data.js'
import { BillsScreen } from './BillsScreen.js'
import { ExpenseForm } from './ExpenseForm.js'
import { FoodOrderScreen } from './FoodOrderScreen.js'
import { LedgerScreen } from './LedgerScreen.js'
import { RecordBillPaymentForm } from './RecordBillPaymentForm.js'
import { SettleUpForm } from './SettleUpForm.js'
import './money.css'

export type MoneyTab = 'balances' | 'bills' | 'spend'

const TABS: readonly { id: MoneyTab; label: string }[] = [
  { id: 'balances', label: 'Balances' },
  { id: 'bills', label: 'Bills' },
  { id: 'spend', label: 'Spend' },
]

export type SpendMode = 'expense' | 'food'

const SPEND_TABS: readonly { id: SpendMode; label: string }[] = [
  { id: 'expense', label: 'Expense' },
  { id: 'food', label: 'Food order' },
]

type Detail =
  | { kind: 'none' }
  /** `amount` is what the row suggested; in the simplified view it is not the pairwise balance. */
  | { kind: 'settle'; to: MemberId | null; amount: Piastres | null }
  | { kind: 'payBill'; bill: Bill; instance: BillInstance }

export interface MoneyHomeProps {
  snapshot: MoneySnapshot
  clock: TrustedClock
  actorId: MemberId
  actions: MoneyActions
  initialTab?: MoneyTab
}

export function MoneyHome({ snapshot, clock, actorId, actions, initialTab }: MoneyHomeProps) {
  const [tab, setTab] = useState<MoneyTab>(initialTab ?? 'balances')
  const [detail, setDetail] = useState<Detail>({ kind: 'none' })
  const [spendMode, setSpendMode] = useState<SpendMode>('expense')
  const [flash, setFlash] = useState<string | null>(null)
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const spendRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const close = (message: string) => {
    setDetail({ kind: 'none' })
    setFlash(message)
  }

  function onTabKey(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (delta === 0) return
    e.preventDefault()
    const next = TABS[(index + delta + TABS.length) % TABS.length]
    if (!next) return
    setTab(next.id)
    setDetail({ kind: 'none' })
    setFlash(null)
    tabRefs.current[next.id]?.focus()
  }

  function onSpendKey(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (delta === 0) return
    e.preventDefault()
    const next = SPEND_TABS[(index + delta + SPEND_TABS.length) % SPEND_TABS.length]
    if (!next) return
    setSpendMode(next.id)
    spendRefs.current[next.id]?.focus()
  }

  return (
    <div className="m-root">
      <header className="m-head">
        <p className="m-eyebrow">Shared office cost</p>
        <h1 className="m-heading">Money</h1>
        <p className="m-sub">
          Split per expense, never assumed three ways. Guests split on the day and carry no balance.
        </p>
      </header>

      <div className="m-tabs" role="tablist" aria-label="Money">
        {TABS.map((t, i) => (
          <button
            key={t.id}
            ref={el => { tabRefs.current[t.id] = el }}
            type="button"
            role="tab"
            id={`money-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls="money-panel"
            tabIndex={tab === t.id ? 0 : -1}
            className={`m-tab${tab === t.id ? ' m-tab-on' : ''}`}
            onClick={() => { setTab(t.id); setDetail({ kind: 'none' }); setFlash(null) }}
            onKeyDown={e => onTabKey(e, i)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {snapshot.readFailed && (
        <p className="m-banner m-banner-danger" role="alert">
          Could not read this device&rsquo;s copy of the money records. What is below may be
          out of date or incomplete.
        </p>
      )}

      {flash !== null && (
        <p className="m-banner m-banner-ok" role="status">
          {flash}
        </p>
      )}

      <div
        className="m-panel"
        role="tabpanel"
        id="money-panel"
        aria-labelledby={`money-tab-${tab}`}
        tabIndex={-1}
      >
        {detail.kind === 'settle' ? (
          <>
            <BackButton onClick={() => setDetail({ kind: 'none' })} />
            <SettleUpForm
              snapshot={snapshot}
              clock={clock}
              actorId={actorId}
              actions={actions}
              {...(detail.to ? { initialTo: detail.to } : {})}
              {...(detail.amount !== null ? { initialAmount: detail.amount } : {})}
              onDone={() => close('Payment recorded.')}
            />
          </>
        ) : detail.kind === 'payBill' ? (
          <>
            <BackButton onClick={() => setDetail({ kind: 'none' })} />
            <RecordBillPaymentForm
              snapshot={snapshot}
              clock={clock}
              actorId={actorId}
              bill={detail.bill}
              instance={detail.instance}
              actions={actions}
              onDone={() => close('Payment recorded, and the shared expense with it.')}
            />
          </>
        ) : tab === 'balances' ? (
          <>
            <LedgerScreen
              snapshot={snapshot}
              clock={clock}
              actorId={actorId}
              onSettleUp={(to, suggested) => {
                setFlash(null)
                setDetail({ kind: 'settle', to, amount: suggested })
              }}
            />
            <div className="m-actions">
              <button
                type="button"
                className="btn"
                onClick={() => { setFlash(null); setDetail({ kind: 'settle', to: null, amount: null }) }}
              >
                Settle up
              </button>
            </div>
          </>
        ) : tab === 'bills' ? (
          <BillsScreen
            snapshot={snapshot}
            clock={clock}
            onRecordPayment={(bill, instance) => {
              setFlash(null)
              setDetail({ kind: 'payBill', bill, instance })
            }}
          />
        ) : (
          <>
            {/* A tablist owes its user the whole pattern, not just the role:
                one tab stop for the set, arrow keys inside it, and a panel each
                tab actually points at. Half of it — roles with no `aria-controls`
                and every tab in the tab order — announces a structure that is
                not there. */}
            <div className="m-tabs m-tabs-sub" role="tablist" aria-label="What are you adding?">
              {SPEND_TABS.map((t, i) => (
                <button
                  key={t.id}
                  ref={el => { spendRefs.current[t.id] = el }}
                  type="button"
                  role="tab"
                  id={`money-spend-tab-${t.id}`}
                  aria-selected={spendMode === t.id}
                  aria-controls="money-spend-panel"
                  tabIndex={spendMode === t.id ? 0 : -1}
                  className={`m-tab${spendMode === t.id ? ' m-tab-on' : ''}`}
                  onClick={() => setSpendMode(t.id)}
                  onKeyDown={e => onSpendKey(e, i)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div
              role="tabpanel"
              id="money-spend-panel"
              aria-labelledby={`money-spend-tab-${spendMode}`}
              tabIndex={-1}
            >
              {spendMode === 'expense' ? (
                <ExpenseForm
                  snapshot={snapshot}
                  clock={clock}
                  actorId={actorId}
                  actions={actions}
                  onDone={() => setFlash('Expense added to the ledger.')}
                />
              ) : (
                <FoodOrderScreen
                  snapshot={snapshot}
                  clock={clock}
                  actorId={actorId}
                  actions={actions}
                  onDone={() => setFlash('Order settled into the ledger.')}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn m-back" onClick={onClick}>
      Back
    </button>
  )
}

export interface MoneyScreenProps {
  db: OfficeDb
  clock: TrustedClock
  actorId: MemberId
  initialTab?: MoneyTab
}

/** Binds the money UI to the local mirror and the outbox. */
export function MoneyScreen({ db, clock, actorId, initialTab }: MoneyScreenProps) {
  const snapshot = useMoneySnapshot(db)
  const actions = useMemo(
    () => actionsFromMutations({ db, now: () => clock.now(), actorId }),
    [db, clock, actorId],
  )
  return (
    <MoneyHome
      snapshot={snapshot}
      clock={clock}
      actorId={actorId}
      actions={actions}
      {...(initialTab ? { initialTab } : {})}
    />
  )
}
